import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Lock, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PermissionMatrix } from "@/components/PermissionMatrix";

const moduleCount = (perms) => Object.values(perms || {}).filter((p) => p.view).length;

export const RolesManager = ({ roles, modules, onChanged }) => {
  const { can } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const open = (mode, role) => setDialog({
    mode, id: role?.id, locked: !!role?.locked,
    name: mode === "duplicate" ? `${role.name} (copia)` : role?.name || "",
    permissions: JSON.parse(JSON.stringify(role?.permissions || {})),
  });

  const save = async () => {
    if (!dialog.name.trim()) { toast.error("Informe o nome do setor"); return; }
    setSaving(true);
    try {
      const payload = { name: dialog.name, permissions: dialog.permissions };
      if (dialog.mode === "edit") await axios.put(`${API}/roles/${dialog.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/roles`, payload, { withCredentials: true });
      toast.success(dialog.mode === "edit" ? "Setor atualizado" : "Setor criado");
      setDialog(null);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar setor"); }
    finally { setSaving(false); }
  };

  const remove = async (role) => {
    if (!window.confirm(`Excluir o setor "${role.name}"?`)) return;
    try {
      await axios.delete(`${API}/roles/${role.id}`, { withCredentials: true });
      toast.success("Setor removido");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover setor"); }
  };

  const readOnly = dialog?.mode === "view";

  return (
    <div className="space-y-4" data-testid="roles-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Cadastre os setores da empresa e as permissoes padrao de cada um. Ao mudar o padrao, os usuarios do setor recebem a mudanca (exceto nos itens ajustados individualmente).</p>
        {can("configuracoes", "create") && (
          <Button onClick={() => open("create")} data-testid="create-role-btn"><Plus className="h-4 w-4 mr-2" /> Novo Setor</Button>
        )}
      </div>
      <Card className="border border-border shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Setor</TableHead>
                <TableHead>Modulos com acesso</TableHead>
                <TableHead>Usuarios</TableHead>
                <TableHead className="w-[150px]">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} data-testid={`role-row-${r.id}`}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {r.name}
                      {r.locked && <Badge variant="secondary" className="text-[10px] gap-1"><Lock className="h-3 w-3" /> Acesso total</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{moduleCount(r.permissions)} de {modules.length}</TableCell>
                  <TableCell className="text-sm" data-testid={`role-users-${r.id}`}>{r.user_count}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Visualizar" onClick={() => open("view", r)} data-testid={`view-role-${r.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      {!r.locked && can("configuracoes", "edit") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar" onClick={() => open("edit", r)} data-testid={`edit-role-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      )}
                      {can("configuracoes", "create") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicar" onClick={() => open("duplicate", r)} data-testid={`duplicate-role-${r.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                      )}
                      {!r.locked && can("configuracoes", "delete") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Excluir" onClick={() => remove(r)} data-testid={`delete-role-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{readOnly ? dialog?.name : dialog?.mode === "edit" ? "Editar Setor" : "Novo Setor"}</DialogTitle>
            <DialogDescription>
              {dialog?.locked ? "O Administrador sempre tem acesso total e nao pode ser alterado." : "Marque as permissoes padrao do setor: os modulos que acessa e o que pode fazer em cada um. Incluir, Editar ou Excluir ativam o Visualizar automaticamente."}
            </DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              {!readOnly && (
                <div className="space-y-2">
                  <Label>Nome do setor *</Label>
                  <Input value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} placeholder="Ex: Compras, Qualidade, Expedicao" data-testid="role-name-input" />
                </div>
              )}
              <PermissionMatrix modules={modules} value={dialog.permissions} readOnly={readOnly}
                onChange={(permissions) => setDialog({ ...dialog, permissions })} />
              <p className="text-xs text-muted-foreground">Em Ordens de Producao, "Editar" inclui iniciar/concluir etapas e sub-etapas.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="role-cancel-btn">{readOnly ? "Fechar" : "Cancelar"}</Button>
            {!readOnly && (
              <Button onClick={save} disabled={saving} data-testid="role-save-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {dialog?.mode === "edit" ? "Salvar Alteracoes" : "Criar Setor"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
